if (!customElements.get('buying-guide')) {
    const ADVANCE_DELAY = 250;

    customElements.define(
        'buying-guide',
        class BuyingGuide extends HTMLElement {
            constructor() {
                super();
                this.collectionUrl = this.dataset.collectionUrl || window.location.pathname;
                this.steps = Array.from(this.querySelectorAll('.buying-guide__step'));
                this.current = 0;
                this.ownedParams = [...new Set(this.steps.map((s) => s.dataset.param).filter(Boolean))];
                this.selections = {};
                this.ownedParams.forEach((p) => (this.selections[p] = new Set()));
                this.advanceTimer = null;

                this.intro = this.querySelector('[data-bg-intro]');
                this.body = this.querySelector('[data-bg-body]');
                this.summary = this.querySelector('[data-bg-summary]');
                this.summaryHeading = this.querySelector('[data-bg-summary-heading]');
                this.empty = this.querySelector('[data-bg-empty]');
                this.countEl = this.querySelector('[data-bg-count]');
                this.progressEl = this.querySelector('[data-bg-progress]');

                this.bindEvents();
                this.hydrateFromUrl();
                this.syncAvailability();
                if (!this.classList.contains('buying-guide--collapsed')) this.begin();
            }

            bindEvents() {
                this.addEventListener('click', (event) => {
                    const skipOption = event.target.closest('[data-bg-skip-option]');
                    if (skipOption) {
                        event.preventDefault();
                        this.skipStep(skipOption);
                        return;
                    }
                    const option = event.target.closest('.buying-guide__option');
                    if (option) {
                        event.preventDefault();
                        this.toggleOption(option);
                        return;
                    }
                    if (event.target.closest('[data-bg-start]')) return this.start();
                    if (event.target.closest('[data-bg-next]')) return this.next();
                    if (event.target.closest('[data-bg-back]')) return this.back();
                    if (event.target.closest('[data-bg-back-empty]')) return this.back();
                    if (event.target.closest('[data-bg-reset]')) return this.reset();
                    if (event.target.closest('[data-bg-restart]')) return this.begin();
                });

                if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
                    subscribe(PUB_SUB_EVENTS.facetUpdate, () => this.onFacetUpdate());
                }
            }

            hydrateFromUrl() {
                const params = new URLSearchParams(window.location.search);
                this.ownedParams.forEach((param) => {
                    params.getAll(param).forEach((value) => this.selections[param].add(value));
                });
                this.steps.forEach((step) => {
                    const set = this.selections[step.dataset.param];
                    step.querySelectorAll('.buying-guide__option').forEach((opt) => {
                        const active = set && set.has(opt.dataset.value);
                        opt.classList.toggle('is-selected', !!active);
                        opt.setAttribute('aria-pressed', active ? 'true' : 'false');
                    });
                });
            }

            begin() {
                this.clearAdvance();
                this.classList.remove('buying-guide--collapsed');
                if (this.intro) this.intro.hidden = true;
                if (this.summary) this.summary.hidden = true;
                if (this.empty) this.empty.hidden = true;
                if (this.body) this.body.hidden = false;
                this.current = 0;
                this.showStep();
            }

            showStep() {
                this.steps.forEach((step, i) => (step.hidden = i !== this.current));
                const step = this.steps[this.current];
                if (!step) return;
                this.showRequired(false);
                if (this.progressEl) {
                    this.progressEl.textContent = `${this.current + 1} / ${this.steps.length}`;
                }
            }

            toggleOption(option) {
                if (option.getAttribute('aria-disabled') === 'true') return;
                const param = option.dataset.param;
                const value = option.dataset.value;
                const step = option.closest('.buying-guide__step');
                const multi = step.dataset.selectMode === 'multi';
                const set = this.selections[param];
                if (!set) return;

                if (multi) {
                    set.has(value) ? set.delete(value) : set.add(value);
                } else {
                    set.clear();
                    set.add(value);
                }

                step.querySelectorAll('.buying-guide__option').forEach((opt) => {
                    const active = set.has(opt.dataset.value);
                    opt.classList.toggle('is-selected', active);
                    opt.setAttribute('aria-pressed', active ? 'true' : 'false');
                });

                this.showRequired(false);
                this.applyFilters();

                if (!multi) this.scheduleAdvance();
            }

            clearAdvance() {
                if (this.advanceTimer) {
                    clearTimeout(this.advanceTimer);
                    this.advanceTimer = null;
                }
            }

            scheduleAdvance() {
                this.clearAdvance();
                this.advanceTimer = setTimeout(() => {
                    this.advanceTimer = null;
                    this.next();
                }, ADVANCE_DELAY);
            }

            clearStep(step) {
                const set = this.selections[step.dataset.param];
                if (set) set.clear();
                step.querySelectorAll('.buying-guide__option').forEach((opt) => {
                    opt.classList.remove('is-selected');
                    if (opt.hasAttribute('aria-pressed')) opt.setAttribute('aria-pressed', 'false');
                });
            }

            skipStep(option) {
                const step = option.closest('.buying-guide__step');
                if (!step) return;
                this.clearAdvance();
                this.clearStep(step);
                this.showRequired(false);
                this.applyFilters();
                this.next(true);
            }

            getAvailability() {
                const map = new Map();
                document.querySelectorAll('#FacetFiltersForm input[type="checkbox"]').forEach((input) => {
                    if (!input.name) return;
                    map.set(`${input.name}\u0000${input.value}`, !input.disabled);
                });
                return map;
            }

            syncAvailability() {
                const map = this.getAvailability();
                if (map.size === 0) return;
                this.steps.forEach((step) => {
                    step.querySelectorAll('.buying-guide__option').forEach((opt) => {
                        if (!opt.dataset.value) return;
                        const key = `${opt.dataset.param}\u0000${opt.dataset.value}`;
                        let available = map.has(key) ? map.get(key) : false;
                        if (opt.classList.contains('is-selected')) available = true;
                        opt.classList.toggle('is-unavailable', !available);
                        opt.setAttribute('aria-disabled', available ? 'false' : 'true');
                    });
                });
            }

            showRequired(show) {
                const el = this.querySelector('[data-bg-required]');
                if (el) el.hidden = !show;
            }

            resetSelections() {
                this.ownedParams.forEach((p) => this.selections[p].clear());
                this.querySelectorAll('.buying-guide__option').forEach((opt) => {
                    opt.classList.remove('is-selected');
                    opt.setAttribute('aria-pressed', 'false');
                });
            }

            clearFilters() {
                const params = new URLSearchParams(window.location.search);
                [...params.keys()].forEach((key) => {
                    if (key.indexOf('filter.') === 0) params.delete(key);
                });
                const search = params.toString();
                if (typeof FacetFiltersForm !== 'undefined' && FacetFiltersForm.renderPage) {
                    FacetFiltersForm.renderPage(search);
                } else {
                    window.location.search = search;
                }
            }

            start() {
                this.resetSelections();
                this.clearFilters();
                this.begin();
            }

            reset() {
                this.resetSelections();
                this.clearFilters();
                this.begin();
            }

            showIntro() {
                if (this.body) this.body.hidden = true;
                if (this.summary) this.summary.hidden = true;
                if (this.empty) this.empty.hidden = true;
                if (this.intro) this.intro.hidden = false;
            }

            buildSearchParams() {
                const params = new URLSearchParams(window.location.search);
                this.ownedParams.forEach((p) => params.delete(p));
                this.ownedParams.forEach((param) => {
                    this.selections[param].forEach((value) => params.append(param, value));
                });
                return params.toString();
            }

            applyFilters() {
                const search = this.buildSearchParams();
                if (typeof FacetFiltersForm !== 'undefined' && FacetFiltersForm.renderPage) {
                    FacetFiltersForm.renderPage(search);
                } else {
                    window.location.search = search;
                }
            }

            readCount() {
                const el =
                    document.getElementById('ProductCountDesktop') || document.getElementById('ProductCount');
                if (!el) return null;
                const match = el.textContent.match(/\d[\d.,]*/);
                return match ? match[0].replace(/[.,]/g, '') : null;
            }

            onFacetUpdate() {
                const count = this.readCount();
                if (count === null) return;
                if (this.countEl) {
                    this.countEl.textContent = this.formatCount(count);
                }
                if (parseInt(count, 10) === 0) {
                    if (this.empty) this.empty.hidden = false;
                } else if (this.empty && !this.empty.hidden) {
                    this.empty.hidden = true;
                }
                this.syncAvailability();
            }

            formatCount(count) {
                const template = this.dataset.countTemplate || '[count]';
                return template.replace(/\[count\]/g, count);
            }

            next(force) {
                this.clearAdvance();
                const step = this.steps[this.current];
                if (!force && step && step.dataset.required === 'true') {
                    const set = this.selections[step.dataset.param];
                    if (!set || set.size === 0) {
                        this.showRequired(true);
                        return;
                    }
                }
                this.showRequired(false);
                if (this.current < this.steps.length - 1) {
                    this.current += 1;
                    this.showStep();
                } else {
                    this.finish();
                }
            }

            back() {
                this.clearAdvance();
                if (this.empty) this.empty.hidden = true;
                if (this.current > 0) {
                    this.current -= 1;
                    if (this.body) this.body.hidden = false;
                    if (this.summary) this.summary.hidden = true;
                    this.showStep();
                } else {
                    this.showIntro();
                }
            }

            finish() {
                if (this.body) this.body.hidden = true;
                if (this.empty) this.empty.hidden = true;
                if (this.summary) this.summary.hidden = false;
                const count = this.readCount() || '';
                if (this.summaryHeading) this.summaryHeading.textContent = this.formatCount(count);
            }
        }
    );
}
